import React, { useState } from 'react';
import { FileSpreadsheet, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, NoteCard } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import '../../styles/design-system.css';

const EXCELtoPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [workbook, setWorkbook] = useState(null);
    const [sheets, setSheets] = useState([]);
    const [selectedSheets, setSelectedSheets] = useState([]);
    const [pageOrientation, setPageOrientation] = useState('landscape');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const wb = XLSX.read(arrayBuffer, { type: 'array' });
                setWorkbook(wb);
                setSheets(wb.SheetNames);
                setSelectedSheets(wb.SheetNames); // Select all by default
                toast.success(`Excel loaded: ${wb.SheetNames.length} sheet(s)`);
            } catch (error) {
                toast.error(`Failed to load Excel: ${error.message}`);
            }
        }
    };

    const toggleSheet = (sheetName) => {
        setSelectedSheets(prev => {
            if (prev.includes(sheetName)) {
                return prev.filter(s => s !== sheetName);
            } else {
                return [...prev, sheetName];
            }
        });
    };

    const convertToPDF = async () => {
        if (!workbook || selectedSheets.length === 0) {
            toast.warning('Please select at least one sheet');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            const pdfDoc = await PDFDocument.create();
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            setProgress(20);

            for (let i = 0; i < selectedSheets.length; i++) {
                const sheetName = selectedSheets[i];
                const worksheet = workbook.Sheets[sheetName];

                setProgress(20 + ((i / selectedSheets.length) * 70));

                // Convert sheet to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Page dimensions
                const [pageWidth, pageHeight] = pageOrientation === 'landscape'
                    ? [842, 595] // A4 landscape
                    : [595, 842]; // A4 portrait

                let page = pdfDoc.addPage([pageWidth, pageHeight]);
                let yPosition = pageHeight - 50;
                const fontSize = 9;
                const lineHeight = fontSize + 3;
                const margin = 40;
                const cellPadding = 5;

                // Add sheet name as title
                page.drawText(sheetName, {
                    x: margin,
                    y: yPosition,
                    size: 14,
                    font: boldFont,
                    color: rgb(0, 0, 0),
                });
                yPosition -= 25;

                // Process rows
                for (const row of jsonData) {
                    if (yPosition < margin + lineHeight) {
                        page = pdfDoc.addPage([pageWidth, pageHeight]);
                        yPosition = pageHeight - 50;
                    }

                    let xPosition = margin;
                    const maxCellWidth = (pageWidth - (2 * margin)) / Math.max(row.length, 1);

                    for (const cell of row) {
                        const cellText = String(cell || '');
                        const truncated = cellText.length > 30 ? cellText.substring(0, 27) + '...' : cellText;

                        page.drawText(truncated, {
                            x: xPosition,
                            y: yPosition,
                            size: fontSize,
                            font: font,
                            color: rgb(0, 0, 0),
                            maxWidth: maxCellWidth - cellPadding
                        });

                        xPosition += maxCellWidth;

                        if (xPosition > pageWidth - margin) break;
                    }

                    yPosition -= lineHeight;
                }
            }

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.xlsx?$/i, '');
            const filename = `${fileName}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('Excel converted! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileSpreadsheet size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    EXCEL to PDF
                </h1>
                <p className="tool-description">
                    Convert Excel spreadsheets (XLSX) to PDF format. Select specific sheets and customize page layout.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['.xlsx', '.xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
                    multiple={false}
                    title="Drop XLSX file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="Excel Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{sheets.length} sheet(s)</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setWorkbook(null);
                                setSheets([]);
                                setSelectedSheets([]);
                            }}
                        >
                            Change File
                        </Button>
                    </div>
                </Alert>
            )}

            {file && sheets.length > 0 && (
                <>
                    {/* Sheet Selection */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Select Sheets to Convert
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
                                {sheets.map((sheetName, index) => (
                                    <label
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--spacing-2)',
                                            padding: 'var(--spacing-3)',
                                            backgroundColor: selectedSheets.includes(sheetName)
                                                ? 'var(--color-accent-subtle)'
                                                : 'var(--color-surface-elevated)',
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            border: selectedSheets.includes(sheetName)
                                                ? '2px solid var(--color-accent)'
                                                : '1px solid var(--color-border)',
                                            transition: 'all var(--transition-base)'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSheets.includes(sheetName)}
                                            onChange={() => toggleSheet(sheetName)}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                                            {sheetName}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                <Button variant="ghost" size="sm" onClick={() => setSelectedSheets(sheets)}>
                                    Select All
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setSelectedSheets([])}>
                                    Deselect All
                                </Button>
                                <Badge variant="info">{selectedSheets.length} selected</Badge>
                            </div>
                        </Card>
                    </div>

                    {/* Page Layout Settings */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Page Layout
                            </h3>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Orientation
                                </label>
                                <select
                                    value={pageOrientation}
                                    onChange={(e) => setPageOrientation(e.target.value)}
                                    className="input"
                                >
                                    <option value="landscape">Landscape (recommended for spreadsheets)</option>
                                    <option value="portrait">Portrait</option>
                                </select>
                            </div>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToPDF}
                            disabled={isProcessing || selectedSheets.length === 0}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to PDF
                        </Button>
                    </div>

                    {/* Info */}
                    <NoteCard variant="info">
                        <strong>Note:</strong> Complex formatting, charts, and formulas may not be perfectly preserved. For best results, use simple data tables.
                    </NoteCard>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default EXCELtoPDF;
